/**
 * Honorarios — Contrato de servicios: Datos del despacho | Machote | Contratos | Generar/revisar.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, ExternalLink, FileText, History, Loader2, Mail, RefreshCw, Save, Search, ShieldCheck, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { DespachoSectionCard } from "@/components/despacho/DespachoUi";
import { Textarea } from "@/components/ui/textarea";
import { applyMachoteDefaults, SERVICE_CONTRACT_CLAUSULAS } from "@/modules/despacho/honorariosContractMachoteDefaults";
import {
  honorariosApi,
  LOAD_PRESTADOR_ERROR,
  parseHonorariosError,
} from "@/modules/despacho/despachoHonorariosApi";
import { isConvenioCoveredMember } from "@/modules/despacho/honorariosConvenioGroup";
import { filterRosterItemsByQuery } from "@/modules/despacho/honorariosRosterSearch";
import api, { detailFromAxiosError } from "@/lib/api";
import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";

const SUB_DESPACHO = "datos_despacho";
const SUB_MACHOTE = "machote";
const SUB_CONTRATOS = "contratos";
const SUB_GENERAR = "generar";
const ACCEPT_PLANTILLA = ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const PREVIEW_PDF_NAME = "Vista_Previa_Contrato_Servicios.pdf";

/** Tres variantes de contrato (mapeo desde condición de Lista). */
const CONTRACT_VARIANT_OPTIONS = [
  {
    id: "honorarios",
    label: "Honorarios con factura",
    hint: "Clientes factura / eventuales con cobro.",
  },
  {
    id: "convenio_otros",
    label: "Convenio / otros conceptos",
    hint: "Convenios de grupo y otros pagos.",
  },
  {
    id: "cortesia",
    label: "Cortesía",
    hint: "Servicios sin cobro de honorarios.",
  },
];

/** Condición de Lista → variante de contrato (alineado al backend). */
const CONDITION_TO_CONTRACT_VARIANT = {
  factura: "honorarios",
  no_recurrente: "honorarios",
  eventual_act_empresarial: "honorarios",
  eventual: "honorarios",
  convenio: "convenio_otros",
  otros_pagos: "convenio_otros",
  cortesia: "cortesia",
};

const CONDITION_SHORT_LABELS = {
  factura: "Factura",
  otros_pagos: "Otros conceptos",
  no_recurrente: "Eventual",
  convenio: "Convenio",
  cortesia: "Cortesía",
  eventual_act_empresarial: "Eventual act. emp.",
  eventual: "Pendiente",
};

const CONDITION_FULL_LABELS = {
  factura: "Clientes con factura CFDI",
  otros_pagos: "Clientes otros conceptos",
  no_recurrente: "Clientes eventuales",
  convenio: "Convenios (grupos)",
  cortesia: "Clientes con cortesía",
  eventual_act_empresarial: "Eventuales act. empresarial",
  eventual: "Pendiente de clasificar",
};

/** Filtros de la pestaña Contratos (agrupan por tipo de PDF). */
const CONTRATOS_VARIANT_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "honorarios", label: "Honorarios / factura" },
  { id: "convenio_otros", label: "Convenio / otros" },
  { id: "cortesia", label: "Cortesía" },
];

function contractVariantFromCondition(billingCondition) {
  const key = String(billingCondition || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return CONDITION_TO_CONTRACT_VARIANT[key] || "honorarios";
}

function contractVariantLabel(variantId) {
  return CONTRACT_VARIANT_OPTIONS.find((v) => v.id === variantId)?.label || variantId || "Honorarios con factura";
}

function conditionShortLabel(billingCondition) {
  const key = String(billingCondition || "").trim().toLowerCase();
  return CONDITION_SHORT_LABELS[key] || CONDITION_FULL_LABELS[key] || key || "—";
}

function rowContractVariant(row) {
  return row?.contract_variant || contractVariantFromCondition(row?.billing_condition);
}

function rowContractVariantLabel(row) {
  return row?.contract_variant_label || contractVariantLabel(rowContractVariant(row));
}

/** Si el honorario se puede editar en la pestaña Contratos. */
function feeEditableForRow(row) {
  const cond = String(row?.billing_condition || "").trim().toLowerCase();
  if (cond === "cortesia") return false;
  if (cond === "convenio" && isConvenioCoveredMember(row)) return false;
  return true;
}

/** Nota de cobro / condición de pago según situación del cliente. */
function paymentConditionNote(row) {
  const cond = String(row?.billing_condition || "").trim().toLowerCase();
  if (cond === "cortesia") {
    return "Sin honorarios · cortesía temporal";
  }
  if (cond === "convenio") {
    if (isConvenioCoveredMember(row)) {
      return "Sin cobro · cubierto por el pagador del grupo";
    }
    return "Honorarios del pagador · cubiertos del grupo sin cobro directo";
  }
  if (cond === "otros_pagos") {
    return "Otros conceptos · honorarios hasta confirmar fecha de pago";
  }
  if (cond === "factura" || cond === "no_recurrente" || cond === "eventual_act_empresarial") {
    return row?.fee_manual ? "Honorario fijo (manual)" : "Honorario precargado (editable)";
  }
  if (cond === "eventual") {
    return "Pendiente de clasificar · revise condición en Lista";
  }
  return "";
}

async function blobLooksLikePdf(blob) {
  if (!blob || blob.size < 5) return false;
  try {
    const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    return head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46;
  } catch {
    return false;
  }
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const EMPTY_PRESTADOR = {
  tipo_persona: "moral",
  nombre: "",
  rfc: "",
  domicilio: "",
  ciudad: "",
  email: "",
  telefono: "",
  curp: "",
  nacionalidad: "mexicana",
  estado_civil: "",
  regimen_patrimonial: "",
  representante: "",
  representante_rfc: "",
  // Legados: se conservan al guardar si ya existían, pero no se muestran en UI.
  cargo_representante: "",
  acta_numero: "",
  acta_fecha: "",
  acta_notario: "",
  acta_ciudad: "",
  poder_numero: "",
  poder_fecha: "",
  poder_notario: "",
};

function mapPrestador(prest = {}) {
  const out = { ...EMPTY_PRESTADOR };
  Object.keys(EMPTY_PRESTADOR).forEach((k) => {
    if (prest[k] != null && prest[k] !== "") out[k] = String(prest[k]);
  });
  if (!out.tipo_persona) {
    out.tipo_persona = (out.rfc || "").replace(/\s/g, "").length === 13 ? "fisica" : "moral";
  }
  return out;
}

/** Solo campos visibles / necesarios — no borra acta/poder legados al guardar. */
const PRESTADOR_SAVE_KEYS = [
  "tipo_persona",
  "nombre",
  "rfc",
  "domicilio",
  "ciudad",
  "email",
  "telefono",
  "curp",
  "nacionalidad",
  "estado_civil",
  "regimen_patrimonial",
  "representante",
  "representante_rfc",
];

function prestadorSavePayload(prest) {
  const out = {};
  PRESTADOR_SAVE_KEYS.forEach((k) => {
    if (prest[k] != null) out[k] = prest[k];
  });
  return out;
}

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function downloadBlobResponse(response, fallbackName) {
  const blob = response?.data;
  if (!blob) return;
  const cd = response.headers?.["content-disposition"] || "";
  const match = /filename="?([^";]+)"?/i.exec(cd);
  const name = match?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function statusBadge(row) {
  if (row.tiene_contrato_firmado) {
    const n = Number(row.signed_files_count || (row.signed_files || []).length || 1);
    return (
      <Badge className="rounded-sm bg-emerald-100 text-emerald-900 hover:bg-emerald-100 text-[10px]">
        {n > 1 ? `Firmado (${n})` : "Firmado"}
      </Badge>
    );
  }
  if (row.tiene_borrador) {
    return (
      <Badge variant="secondary" className="rounded-sm text-[10px]">
        Borrador
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="rounded-sm text-[10px] text-slate-600">
      Sin contrato
    </Badge>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function HonorariosContratoServiciosPanel({
  readOnly = false,
  canViewAmounts = true,
}) {
  const [subTab, setSubTab] = useState(SUB_DESPACHO);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [listFilter, setListFilter] = useState("sin_contrato");
  const [contratosVariantFilter, setContratosVariantFilter] = useState("all");
  /** Buscador local por nombre o RFC en pestaña Contratos. */
  const [contratosSearch, setContratosSearch] = useState("");
  /** Borradores locales de honorarios/fecha en pestaña Contratos (clave = fiscal_client_id). */
  const [feeDrafts, setFeeDrafts] = useState({});
  const [dateDrafts, setDateDrafts] = useState({});
  const [template, setTemplate] = useState(null);
  const [body, setBody] = useState(() => applyMachoteDefaults().body);
  const [emailSubject, setEmailSubject] = useState(() => applyMachoteDefaults().emailSubject);
  const [emailBody, setEmailBody] = useState(() => applyMachoteDefaults().emailBody);
  const [increasePct, setIncreasePct] = useState("10");
  const [machoteVariant, setMachoteVariant] = useState("honorarios");
  const [prestador, setPrestador] = useState({ ...EMPTY_PRESTADOR });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState("");
  const [genClientId, setGenClientId] = useState("");
  const [fechaInicio, setFechaInicio] = useState(todayIso);
  const [previewText, setPreviewText] = useState("");
  const [previewMeta, setPreviewMeta] = useState(null);
  const [validation, setValidation] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfPreviewBlob, setPdfPreviewBlob] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileRefs = useRef({});
  const plantillaInputRef = useRef(null);
  const pdfPreviewUrlRef = useRef("");
  const sendPreviewUrlRef = useRef("");
  /** Cliente para diálogo de historial de PDFs firmados. */
  const [signedHistoryRow, setSignedHistoryRow] = useState(null);
  /** Cliente para diálogo revisar/enviar contrato. */
  const [sendContractRow, setSendContractRow] = useState(null);
  const [sendContractBusy, setSendContractBusy] = useState(false);
  const [sendPreviewBusy, setSendPreviewBusy] = useState(false);
  const [sendValidation, setSendValidation] = useState(null);
  const [sendAllowIncomplete, setSendAllowIncomplete] = useState(false);
  const [sendValidationBusy, setSendValidationBusy] = useState(false);
  const previewSectionRef = useRef(null);
  const embeddedPreview = useMemo(() => isEmbeddedBrowserContext(), []);

  const revokePdfPreview = () => {
    if (pdfPreviewUrlRef.current) {
      URL.revokeObjectURL(pdfPreviewUrlRef.current);
      pdfPreviewUrlRef.current = "";
    }
    setPdfPreviewUrl("");
    setPdfPreviewBlob(null);
  };

  const applyTemplateState = (tpl, variant = machoteVariant) => {
    const machote = applyMachoteDefaults(tpl || {});
    setTemplate(machote.template);
    setIncreasePct(machote.increasePct);
    const items = tpl?.contract_variants?.items || {};
    const entry = items[variant];
    if (entry?.body) {
      setBody(entry.body);
      setEmailSubject(entry.email_subject || machote.emailSubject);
      setEmailBody(entry.email_body || machote.emailBody);
    } else {
      setBody(machote.body);
      setEmailSubject(machote.emailSubject);
      setEmailBody(machote.emailBody);
    }
  };

  const switchMachoteVariant = (nextVariant) => {
    setMachoteVariant(nextVariant);
    if (template) {
      const items = template?.contract_variants?.items || {};
      const entry = items[nextVariant];
      const machote = applyMachoteDefaults(template);
      if (entry?.body) {
        setBody(entry.body);
        setEmailSubject(entry.email_subject || machote.emailSubject);
        setEmailBody(entry.email_body || machote.emailBody);
      } else {
        setBody(machote.body);
        setEmailSubject(machote.emailSubject);
        setEmailBody(machote.emailBody);
      }
    }
  };

  const currentVariantMeta = template?.contract_variants?.items?.[machoteVariant] || null;

  const handleLoadSuggestedVariant = () => {
    if (readOnly || machoteVariant === "honorarios") return;
    const entry = template?.contract_variants?.items?.[machoteVariant];
    if (!entry?.suggested_body) {
      toast.message("No hay modelo sugerido para esta variante.");
      return;
    }
    setBody(entry.suggested_body);
    if (entry.suggested_email_subject) setEmailSubject(entry.suggested_email_subject);
    if (entry.suggested_email_body) setEmailBody(entry.suggested_email_body);
    toast.message("Modelo sugerido cargado en el editor. Guarde para usarlo al generar.");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Cargas independientes: un fallo en lista/machote no debe dejar vacío el prestador.
      const [listRes, tplRes, prestRes] = await Promise.allSettled([
        honorariosApi.listServiceContracts({ contractFilter: "all" }),
        honorariosApi.getServiceContractTemplate(),
        honorariosApi.getServiceContractPrestador({ timeout: 25000 }),
      ]);
      if (listRes.status === "fulfilled") {
        setItems(listRes.value.items || []);
        setSummary(listRes.value.summary || {});
      } else {
        toast.error(parseHonorariosError(listRes.reason).message);
      }
      if (tplRes.status === "fulfilled") {
        applyTemplateState(tplRes.value);
      } else {
        toast.error(parseHonorariosError(tplRes.reason).message);
      }
      if (prestRes.status === "fulfilled") {
        setPrestador(mapPrestador(prestRes.value));
      } else {
        toast.error(
          parseHonorariosError(prestRes.reason, { fallbackMessage: LOAD_PRESTADOR_ERROR }).message,
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (pdfPreviewUrlRef.current) URL.revokeObjectURL(pdfPreviewUrlRef.current);
    };
  }, [load]);

  const filteredItems = useMemo(() => {
    if (listFilter === "sin_contrato") {
      return items.filter((r) => !r.tiene_contrato_firmado);
    }
    if (listFilter === "firmado") {
      return items.filter((r) => r.tiene_contrato_firmado);
    }
    if (listFilter === "borrador") {
      return items.filter((r) => r.tiene_borrador && !r.tiene_contrato_firmado);
    }
    return items;
  }, [items, listFilter]);

  const contratosItems = useMemo(() => {
    const byVariant = contratosVariantFilter === "all"
      ? items
      : items.filter((r) => rowContractVariant(r) === contratosVariantFilter);
    return filterRosterItemsByQuery(byVariant, contratosSearch);
  }, [items, contratosVariantFilter, contratosSearch]);

  const contratosCounts = useMemo(() => {
    const counts = { all: items.length, honorarios: 0, convenio_otros: 0, cortesia: 0 };
    items.forEach((r) => {
      const v = rowContractVariant(r);
      if (counts[v] != null) counts[v] += 1;
    });
    return counts;
  }, [items]);

  const clientOptions = useMemo(
    () => (items || []).map((r) => ({
      id: r.fiscal_client_id,
      label: `${r.client_name || "Cliente"}${r.client_rfc ? ` · ${r.client_rfc}` : ""}`,
      ...r,
    })),
    [items],
  );

  const selectedRow = useMemo(
    () => items.find((r) => r.fiscal_client_id === genClientId) || null,
    [items, genClientId],
  );

  const selectedContractVariantId = selectedRow ? rowContractVariant(selectedRow) : "";
  const selectedContractVariantLabel = selectedRow ? rowContractVariantLabel(selectedRow) : "";

  const selectClientForContract = (row) => {
    if (!row?.fiscal_client_id) return;
    setGenClientId(row.fiscal_client_id);
    const start = (
      row.fecha_inicio_contrato
      || row.operations_start_at
      || ""
    ).slice(0, 10);
    if (start) setFechaInicio(start);
    setPreviewText("");
    setPreviewMeta(null);
    setValidation(null);
  };

  const feeDraftValue = (row) => {
    const fid = row.fiscal_client_id;
    if (Object.prototype.hasOwnProperty.call(feeDrafts, fid)) return feeDrafts[fid];
    return String(row.monthly_fee ?? 0);
  };

  const dateDraftValue = (row) => {
    const fid = row.fiscal_client_id;
    if (Object.prototype.hasOwnProperty.call(dateDrafts, fid)) return dateDrafts[fid];
    return (
      row.fecha_inicio_contrato
      || row.operations_start_at
      || ""
    ).slice(0, 10);
  };

  const saveContratoRosterFields = async (row, { fee, date } = {}) => {
    const rosterId = row.roster_id;
    if (!rosterId) {
      toast.error("No hay fila de Lista asociada. Sincronice Lista de clientes.");
      return;
    }
    const patch = {};
    if (fee !== undefined && feeEditableForRow(row)) {
      const n = Number(fee);
      patch.monthly_fee = Number.isFinite(n) && n >= 0 ? n : 0;
      patch.fee_manual = true;
    }
    if (date !== undefined) {
      patch.operations_start_at = String(date || "").slice(0, 10);
    }
    if (!Object.keys(patch).length) return;

    await withRow(row.fiscal_client_id, async () => {
      const updated = await honorariosApi.updateClientRoster(rosterId, patch);
      setItems((prev) => prev.map((r) => {
        if (r.fiscal_client_id !== row.fiscal_client_id) return r;
        return {
          ...r,
          monthly_fee: updated?.monthly_fee ?? patch.monthly_fee ?? r.monthly_fee,
          fee_manual: updated?.fee_manual ?? patch.fee_manual ?? r.fee_manual,
          operations_start_at: updated?.operations_start_at ?? patch.operations_start_at ?? r.operations_start_at,
          fecha_inicio_contrato: updated?.operations_start_at
            || patch.operations_start_at
            || r.fecha_inicio_contrato,
        };
      }));
      setFeeDrafts((d) => {
        const next = { ...d };
        delete next[row.fiscal_client_id];
        return next;
      });
      setDateDrafts((d) => {
        const next = { ...d };
        delete next[row.fiscal_client_id];
        return next;
      });
      if (patch.operations_start_at && genClientId === row.fiscal_client_id) {
        setFechaInicio(patch.operations_start_at);
      }
      toast.success("Honorarios / fecha guardados en Lista.");
    });
  };

  const sampleClient = useMemo(() => {
    if (selectedRow) return selectedRow;
    return items.find((r) => Number(r.monthly_fee) > 0) || items[0] || null;
  }, [items, selectedRow]);

  // Predeterminado desde Lista (operations_start / fecha_inicio_contrato del listado).
  useEffect(() => {
    if (!sampleClient) return;
    const fromClient = (
      sampleClient.fecha_inicio_contrato
      || sampleClient.operations_start_at
      || ""
    ).slice(0, 10);
    if (!fromClient) return;
    setFechaInicio((prev) => {
      // Solo precargar si aún es «hoy» (estado inicial) o vacío.
      if (!prev || prev === todayIso()) return fromClient;
      return prev;
    });
  }, [sampleClient?.fiscal_client_id, sampleClient?.fecha_inicio_contrato, sampleClient?.operations_start_at]);

  const clausulas = useMemo(() => {
    const fromApi = template?.contrato_clausulas;
    if (Array.isArray(fromApi) && fromApi.length) return fromApi;
    return SERVICE_CONTRACT_CLAUSULAS;
  }, [template]);

  const handleSavePrestador = async () => {
    if (readOnly) return;
    setBusy(true);
    try {
      const data = await honorariosApi.saveServiceContractPrestador(prestadorSavePayload(prestador));
      setPrestador(mapPrestador(data));
      toast.success("Datos del despacho (prestador) guardados.");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleReloadFromProfile = async () => {
    setBusy(true);
    try {
      const data = await honorariosApi.getServiceContractPrestador({
        refreshFromProfile: true,
        timeout: 25000,
      });
      const mapped = mapPrestador(data);
      setPrestador(mapped);
      const filled = [mapped.nombre, mapped.rfc, mapped.domicilio, mapped.email].filter(Boolean).length;
      if (filled) {
        toast.success("Datos precargados desde perfil / licencia del espacio.");
      } else {
        toast.message(
          "No hay datos en el perfil del espacio. Complete Configuración → Sistema (razón social, RFC, domicilio) o capture aquí.",
        );
      }
    } catch (err) {
      const code = err?.code || err?.name || "";
      const isTimeout =
        code === "ECONNABORTED"
        || /timeout/i.test(String(err?.message || ""))
        || err?.response?.status === 504;
      toast.error(
        isTimeout
          ? "La precarga tardó demasiado. Revise la conexión o Configuración → Sistema e intente de nuevo."
          : parseHonorariosError(err, { fallbackMessage: LOAD_PRESTADOR_ERROR }).message,
      );
    } finally {
      setBusy(false);
    }
  };

  const setPrestadorField = (key, value) => {
    setPrestador((p) => ({ ...p, [key]: value }));
  };

  const handleSaveTemplate = async () => {
    if (readOnly) return;
    setBusy(true);
    try {
      const pct = Number(increasePct);
      const tpl = await honorariosApi.saveServiceContractTemplate({
        body,
        email_subject: emailSubject,
        email_body: emailBody,
        annual_increase_pct: Number.isFinite(pct) ? pct : 10,
        contract_variant: machoteVariant,
      });
      setTemplate(tpl);
      applyTemplateState(tpl, machoteVariant);
      toast.success(
        machoteVariant === "honorarios"
          ? "Machote de honorarios guardado."
          : `Variante «${CONTRACT_VARIANT_OPTIONS.find((v) => v.id === machoteVariant)?.label || machoteVariant}» guardada.`,
      );
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleClearVariant = async () => {
    if (readOnly || machoteVariant === "honorarios") return;
    if (!window.confirm("¿Quitar el texto propio de esta variante y volver al machote general?")) return;
    setBusy(true);
    try {
      const tpl = await honorariosApi.saveServiceContractTemplate({
        contract_variant: machoteVariant,
        clear_variant: true,
      });
      setTemplate(tpl);
      applyTemplateState(tpl, machoteVariant);
      toast.success("Variante restablecida al machote general.");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleResetTemplate = async () => {
    if (readOnly) return;
    if (!window.confirm("¿Restablecer el machote predeterminado del sistema?")) return;
    setBusy(true);
    try {
      const tpl = await honorariosApi.saveServiceContractTemplate({ reset_to_default: true });
      applyTemplateState(tpl);
      toast.success("Machote restablecido al predeterminado.");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handlePreviewPdfEmpty = async ({ autoDownload = false } = {}) => {
    setBusy(true);
    try {
      const response = await honorariosApi.previewServiceContractPdf(
        sampleClient?.fiscal_client_id || "",
        {
          body,
          annualIncreasePct: increasePct,
          fechaInicio,
          contractVariant: machoteVariant,
        },
      );
      let blob = response?.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([blob], { type: "application/pdf" });
      }
      if (!(await blobLooksLikePdf(blob))) {
        const msg = await detailFromAxiosError({ response: { data: blob, status: response?.status } });
        throw new Error(msg || "La respuesta del servidor no es un PDF válido");
      }
      revokePdfPreview();
      const url = URL.createObjectURL(blob);
      pdfPreviewUrlRef.current = url;
      setPdfPreviewUrl(url);
      setPdfPreviewBlob(blob);
      // En Simple Browser / webview el <object> PDF a menudo no se muestra (o rompe la sesión).
      if (autoDownload || embeddedPreview) {
        triggerBlobDownload(blob, PREVIEW_PDF_NAME);
        toast.success(
          embeddedPreview && !autoDownload
            ? "PDF listo: descargado (el visor del IDE no embebe PDFs con seguridad)."
            : "Descarga del PDF iniciada.",
        );
      } else {
        toast.success("Vista previa lista. Revise el PDF abajo o descárguelo.");
        requestAnimationFrame(() => {
          previewSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    } catch (err) {
      const msg =
        (await detailFromAxiosError(err))
        || parseHonorariosError(err).message
        || err?.message
        || "No se pudo generar la vista previa del PDF.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadPreviewPdf = async () => {
    if (pdfPreviewBlob) {
      triggerBlobDownload(pdfPreviewBlob, PREVIEW_PDF_NAME);
      toast.success("Descarga iniciada.");
      return;
    }
    await handlePreviewPdfEmpty({ autoDownload: true });
  };

  const handleOpenPreviewPdfTab = () => {
    if (!pdfPreviewUrl) {
      toast.message("Primero pulse «Ver PDF del contrato».");
      return;
    }
    window.open(pdfPreviewUrl, "_blank", "noopener,noreferrer");
  };

  const handleUploadPlantilla = async (file) => {
    if (readOnly || !file) return;
    setUploadBusy(true);
    try {
      const data = await honorariosApi.uploadServiceContractPlantilla(file);
      applyTemplateState(data);
      toast.success(data.import_note || "Plantilla del despacho cargada.");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setUploadBusy(false);
      if (plantillaInputRef.current) plantillaInputRef.current.value = "";
    }
  };

  const handleDownloadPlantilla = async () => {
    setUploadBusy(true);
    try {
      const response = await honorariosApi.downloadServiceContractPlantilla();
      downloadBlobResponse(response, template?.plantilla_empresa?.filename || "plantilla_empresa.docx");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setUploadBusy(false);
    }
  };

  const handleSelectModelo = async (modelo) => {
    if (readOnly) return;
    setUploadBusy(true);
    try {
      const data = await honorariosApi.setServiceContractModeloActivo(modelo);
      applyTemplateState(data);
      toast.success(
        modelo === "empresa"
          ? "Se usará el modelo de la empresa (plantilla propia)."
          : "Se usará el modelo del sistema.",
      );
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setUploadBusy(false);
    }
  };

  const handleReviewText = async () => {
    if (!genClientId) {
      toast.error("Seleccione un cliente.");
      return;
    }
    setBusy(true);
    try {
      const data = await honorariosApi.previewServiceContractText(genClientId, {
        fechaInicio,
      });
      setPreviewText(data.text || "");
      setPreviewMeta(data);
      setValidation(data.validation || null);
      if (data.fecha_inicio_iso) setFechaInicio(data.fecha_inicio_iso);
      const req = data.validation?.required_count || 0;
      if (req > 0) {
        toast.message(`Contrato listo para revisión · faltan ${req} dato(s) indispensable(s).`);
      } else {
        toast.success("Contrato llenado para revisión.");
      }
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = async (allowIncomplete = false) => {
    if (readOnly || !genClientId) return;
    setBusy(true);
    try {
      const response = await honorariosApi.generateServiceContract(genClientId, {
        fechaInicio,
        allowIncomplete,
      });
      downloadBlobResponse(response, "contrato.pdf");
      toast.success(allowIncomplete ? "PDF generado (con datos incompletos)." : "PDF generado (borrador).");
      await load();
      const data = await honorariosApi.previewServiceContractText(genClientId, { fechaInicio });
      setPreviewText(data.text || "");
      setPreviewMeta(data);
      setValidation(data.validation || null);
    } catch (err) {
      let detail = err?.response?.data?.detail;
      const raw = err?.response?.data;
      if (raw && typeof Blob !== "undefined" && raw instanceof Blob) {
        try {
          const parsed = JSON.parse(await raw.text());
          detail = parsed.detail || parsed;
        } catch {
          detail = null;
        }
      }
      if (detail?.validation || detail?.code === "CONTRACT_DATA_INCOMPLETE") {
        setValidation(detail.validation || null);
        toast.error(detail.message || "Faltan datos indispensables de las partes.");
      } else {
        toast.error(parseHonorariosError(err).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSendSelected = async (force = false) => {
    if (readOnly || !genClientId || !selectedRow) return;
    setSendContractRow(selectedRow);
  };

  const withRow = async (fiscalClientId, fn) => {
    setRowBusy(fiscalClientId);
    try {
      await fn();
      await load();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setRowBusy("");
    }
  };

  const revokeSendPreviewUrl = useCallback(() => {
    if (sendPreviewUrlRef.current) {
      URL.revokeObjectURL(sendPreviewUrlRef.current);
      sendPreviewUrlRef.current = "";
    }
  }, []);

  const closeSendContractDialog = useCallback(() => {
    revokeSendPreviewUrl();
    setSendContractRow(null);
    setSendContractBusy(false);
    setSendPreviewBusy(false);
    setSendValidation(null);
    setSendAllowIncomplete(false);
    setSendValidationBusy(false);
  }, [revokeSendPreviewUrl]);

  const sendContractFechaInicio = useMemo(() => {
    if (!sendContractRow) return "";
    return dateDraftValue(sendContractRow)
      || (sendContractRow.fecha_inicio_contrato || sendContractRow.operations_start_at || "").slice(0, 10);
  }, [sendContractRow, dateDrafts]);

  useEffect(() => {
    if (!sendContractRow?.fiscal_client_id) {
      setSendValidation(null);
      return undefined;
    }
    let cancelled = false;
    setSendValidationBusy(true);
    setSendAllowIncomplete(false);
    (async () => {
      try {
        const data = await honorariosApi.previewServiceContractText(
          sendContractRow.fiscal_client_id,
          { fechaInicio: sendContractFechaInicio || undefined },
        );
        if (!cancelled) setSendValidation(data.validation || null);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (!cancelled) {
          setSendValidation(detail?.validation || null);
          if (!detail?.validation) {
            toast.error(parseHonorariosError(err).message);
          }
        }
      } finally {
        if (!cancelled) setSendValidationBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sendContractRow?.fiscal_client_id, sendContractFechaInicio]);

  useEffect(() => () => {
    if (sendPreviewUrlRef.current) URL.revokeObjectURL(sendPreviewUrlRef.current);
  }, []);

  const handlePreviewSendContractPdf = async () => {
    if (!sendContractRow?.fiscal_client_id) return;
    setSendPreviewBusy(true);
    try {
      const response = await honorariosApi.previewServiceContractPdf(
        sendContractRow.fiscal_client_id,
        { fechaInicio: sendContractFechaInicio || undefined },
      );
      let blob = response?.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([blob], { type: "application/pdf" });
      }
      revokeSendPreviewUrl();
      const url = URL.createObjectURL(blob);
      sendPreviewUrlRef.current = url;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Vista previa del PDF abierta. Revise antes de confirmar el envío.");
    } catch (err) {
      toast.error(parseHonorariosError(err).message || "No se pudo generar la vista previa.");
    } finally {
      setSendPreviewBusy(false);
    }
  };

  const handleConfirmSendContract = async () => {
    if (!sendContractRow?.fiscal_client_id) return;
    const incomplete = sendValidation && sendValidation.ok === false;
    setSendContractBusy(true);
    try {
      const result = await honorariosApi.sendServiceContractEmail(
        sendContractRow.fiscal_client_id,
        {
          force: sendContractRow.email_status === "sent",
          allowIncomplete: incomplete || sendAllowIncomplete,
          fechaInicio: sendContractFechaInicio || undefined,
        },
      );
      if (result.ok) {
        toast.success(
          `Contrato enviado a ${(result.to_emails || []).join(", ") || sendContractRow.email || "cliente"}.`,
        );
        closeSendContractDialog();
        await load();
      } else {
        toast.error(result.detail || "No se pudo enviar.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.validation || detail?.code === "CONTRACT_DATA_INCOMPLETE") {
        setSendValidation(detail.validation || sendValidation);
        setSendAllowIncomplete(true);
        toast.error(
          detail.message
          || "Aún faltan datos mínimos (nombre, RFC, domicilio). Complételos en Despacho o Clientes.",
        );
      } else {
        toast.error(parseHonorariosError(err).message);
      }
    } finally {
      setSendContractBusy(false);
    }
  };

  const signedHistoryFiles = useMemo(() => {
    if (!signedHistoryRow) return [];
    const live = items.find((r) => r.fiscal_client_id === signedHistoryRow.fiscal_client_id)
      || signedHistoryRow;
    const files = Array.isArray(live.signed_files) ? live.signed_files : [];
    if (files.length) return files;
    if (live.tiene_contrato_firmado && live.signed_filename) {
      return [{
        file_id: "latest",
        filename: live.signed_filename,
        uploaded_at: live.signed_uploaded_at || "",
      }];
    }
    return [];
  }, [signedHistoryRow, items]);

  const handleForceAudit = async () => {
    setBusy(true);
    try {
      await api.post("/internal/audit", { module_name: "honorarios_service_contracts" });
      toast.success("Auditoría de integridad solicitada.");
      await load();
    } catch (err) {
      toast.error(parseHonorariosError(err).message || "No se pudo forzar la auditoría.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="honorarios-contrato-servicios">
      <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
        <TabsList className="!flex h-auto w-full max-w-4xl flex-wrap gap-1 mb-3">
          <TabsTrigger value={SUB_DESPACHO} className="text-xs rounded-sm flex-1" data-testid="subtab-datos-despacho">
            Datos del despacho
          </TabsTrigger>
          <TabsTrigger value={SUB_MACHOTE} className="text-xs rounded-sm flex-1" data-testid="subtab-machote">
            Machote
          </TabsTrigger>
          <TabsTrigger value={SUB_CONTRATOS} className="text-xs rounded-sm flex-1" data-testid="subtab-contratos">
            Contratos
          </TabsTrigger>
          <TabsTrigger value={SUB_GENERAR} className="text-xs rounded-sm flex-1" data-testid="subtab-generar">
            Generar / revisar
          </TabsTrigger>
        </TabsList>

        <TabsContent value={SUB_DESPACHO}>
          <DespachoSectionCard
            title="Datos del despacho (prestador de servicios)"
            subtitle="Se precargan desde el perfil del espacio (Configuración), la licencia o la ficha propia. Indispensables: nombre, RFC y domicilio. Persona moral: también el representante (el RFC del representante es opcional)."
          >
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm h-8 text-xs"
                disabled={readOnly || busy}
                onClick={handleReloadFromProfile}
                data-testid="prestador-reload-profile"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                Cargar desde perfil / licencia
              </Button>
              <span className="text-[10px] text-slate-500">
                Útil si actualizó Configuración → Sistema y quiere traer razón social, RFC y domicilio.
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Tipo de persona</Label>
                <Select
                  value={prestador.tipo_persona || "moral"}
                  onValueChange={(v) => setPrestadorField("tipo_persona", v)}
                  disabled={readOnly || busy}
                >
                  <SelectTrigger className="mt-1 h-8 text-xs rounded-sm" data-testid="prestador-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="moral">Persona moral</SelectItem>
                    <SelectItem value="fisica">Persona física</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Ciudad de celebración</Label>
                <Input
                  className="mt-1 h-8 text-xs rounded-sm"
                  value={prestador.ciudad}
                  disabled={readOnly || busy}
                  onChange={(e) => setPrestadorField("ciudad", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Razón social / nombre completo</Label>
                <Input
                  className="mt-1 h-8 text-xs rounded-sm"
                  value={prestador.nombre}
                  disabled={readOnly || busy}
                  onChange={(e) => setPrestadorField("nombre", e.target.value)}
                  data-testid="prestador-nombre"
                />
              </div>
              <div>
                <Label className="text-xs">RFC</Label>
                <Input
                  className="mt-1 h-8 text-xs rounded-sm font-mono"
                  value={prestador.rfc}
                  disabled={readOnly || busy}
                  onChange={(e) => setPrestadorField("rfc", e.target.value.toUpperCase())}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Domicilio fiscal / convencional</Label>
                <Input
                  className="mt-1 h-8 text-xs rounded-sm"
                  value={prestador.domicilio}
                  disabled={readOnly || busy}
                  onChange={(e) => setPrestadorField("domicilio", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Correo electrónico</Label>
                <Input
                  className="mt-1 h-8 text-xs rounded-sm"
                  value={prestador.email}
                  disabled={readOnly || busy}
                  onChange={(e) => setPrestadorField("email", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Teléfono</Label>
                <Input
                  className="mt-1 h-8 text-xs rounded-sm"
                  value={prestador.telefono}
                  disabled={readOnly || busy}
                  onChange={(e) => setPrestadorField("telefono", e.target.value)}
                />
              </div>

              {prestador.tipo_persona === "fisica" ? (
                <>
                  <div>
                    <Label className="text-xs">CURP</Label>
                    <Input
                      className="mt-1 h-8 text-xs rounded-sm font-mono"
                      value={prestador.curp}
                      disabled={readOnly || busy}
                      onChange={(e) => setPrestadorField("curp", e.target.value.toUpperCase())}
                      maxLength={18}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nacionalidad</Label>
                    <Input
                      className="mt-1 h-8 text-xs rounded-sm"
                      value={prestador.nacionalidad}
                      disabled={readOnly || busy}
                      onChange={(e) => setPrestadorField("nacionalidad", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Estado civil</Label>
                    <Input
                      className="mt-1 h-8 text-xs rounded-sm"
                      value={prestador.estado_civil}
                      disabled={readOnly || busy}
                      onChange={(e) => setPrestadorField("estado_civil", e.target.value)}
                      placeholder="Soltero(a), casado(a)…"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Régimen patrimonial (si aplica)</Label>
                    <Input
                      className="mt-1 h-8 text-xs rounded-sm"
                      value={prestador.regimen_patrimonial}
                      disabled={readOnly || busy}
                      onChange={(e) => setPrestadorField("regimen_patrimonial", e.target.value)}
                      placeholder="Sociedad conyugal / separación de bienes"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="sm:col-span-2 mt-1">
                    <p className="m-0 text-[11px] font-medium text-[#0F2D5C]">Representante legal</p>
                    <p className="m-0 mt-0.5 text-[10px] text-slate-500">
                      Quien firma el contrato por la persona moral. No se requieren datos de acta constitutiva ni poder notarial.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Nombre del representante</Label>
                    <Input
                      className="mt-1 h-8 text-xs rounded-sm"
                      value={prestador.representante}
                      disabled={readOnly || busy}
                      onChange={(e) => setPrestadorField("representante", e.target.value)}
                      data-testid="prestador-representante"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">RFC del representante</Label>
                    <Input
                      className="mt-1 h-8 text-xs rounded-sm font-mono"
                      value={prestador.representante_rfc}
                      disabled={readOnly || busy}
                      onChange={(e) => setPrestadorField("representante_rfc", e.target.value.toUpperCase())}
                      maxLength={13}
                      data-testid="prestador-representante-rfc"
                    />
                  </div>
                </>
              )}
            </div>
            {!readOnly ? (
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-sm text-xs"
                  disabled={busy}
                  onClick={handleSavePrestador}
                  data-testid="prestador-guardar"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Guardar datos del despacho
                </Button>
              </div>
            ) : null}
          </DespachoSectionCard>
        </TabsContent>

        <TabsContent value={SUB_MACHOTE}>
          <div className="space-y-6" data-testid="machote-diseno-laboral">
            <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-[#0F2D5C]">
                <FileText className="h-4 w-4" />
                Contrato de prestación de servicios — modelo del sistema
              </div>
              <p className="text-xs text-muted-foreground m-0">
                Así se verá el PDF que el sistema generará por cliente de honorarios, usando los datos del
                despacho (prestador) y la ficha del cliente (honorario, RFC, domicilio, etc.).
              </p>

              <div className="rounded-md border bg-slate-50 p-3 text-xs space-y-2">
                <p className="m-0 font-medium text-slate-700">Datos de muestra en la vista previa</p>
                <p className="m-0">
                  <span className="text-slate-500">Prestador:</span>{" "}
                  {prestador.nombre || "— (complete en Datos del despacho)"}
                </p>
                <p className="m-0">
                  <span className="text-slate-500">Tipo:</span>{" "}
                  {prestador.tipo_persona === "fisica" ? "Persona física" : "Persona moral"}
                </p>
                <p className="m-0">
                  <span className="text-slate-500">Representante:</span>{" "}
                  {prestador.tipo_persona === "fisica"
                    ? "No aplica (comparece por su propio derecho)"
                    : (prestador.representante || "(espacio en blanco en PDF)")}
                  {prestador.tipo_persona !== "fisica" && prestador.representante_rfc
                    ? ` · RFC ${prestador.representante_rfc}`
                    : ""}
                </p>
                <p className="m-0">
                  <span className="text-slate-500">RFC prestador:</span> {prestador.rfc || "—"}
                </p>
                <p className="m-0">
                  <span className="text-slate-500">Cliente ejemplo:</span>{" "}
                  {sampleClient?.client_name || "— (registre clientes en Lista)"}
                  {sampleClient?.client_rfc ? ` · ${sampleClient.client_rfc}` : ""}
                </p>
                <p className="m-0">
                  <span className="text-slate-500">Honorario mensual:</span>{" "}
                  {sampleClient
                    ? (canViewAmounts ? money(sampleClient.monthly_fee) : "•••")
                    : "—"}
                </p>
                <p className="m-0">
                  <span className="text-slate-500">Incremento anual:</span> {increasePct || "10"}%
                </p>
                <p className="m-0">
                  <span className="text-slate-500">Inicio (Lista):</span>{" "}
                  {(
                    sampleClient?.fecha_inicio_contrato
                    || sampleClient?.operations_start_at
                    || ""
                  ).slice(0, 10) || "— (defina «Inicio operaciones / contrato» en Lista)"}
                </p>
                <p className="m-0">
                  <span className="text-slate-500">Ciudad:</span> {prestador.ciudad || "—"}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-700 m-0">Cláusulas incluidas</p>
                <ul className="m-0 pl-4 space-y-1 text-xs text-slate-600 list-disc">
                  {clausulas.map((c) => (
                    <li key={c.titulo || c.texto}>
                      <span className="font-medium text-slate-800">{c.titulo}:</span> {c.texto}
                    </li>
                  ))}
                </ul>
              </div>

              {template?.notes ? (
                <p className="m-0 text-[11px] text-slate-600 flex items-start gap-1.5 rounded-md border border-sky-200 bg-sky-50/90 px-3 py-2">
                  <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#0F2D5C]" aria-hidden />
                  <span>{template.notes}</span>
                </p>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  className="bg-[#0F2D5C] hover:bg-[#0a2247]"
                  size="sm"
                  disabled={busy}
                  onClick={() => handlePreviewPdfEmpty({ autoDownload: false })}
                  data-testid="contrato-preview-machote"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                  Ver PDF del contrato
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={handleDownloadPreviewPdf}
                  data-testid="contrato-download-preview-pdf"
                >
                  <Download className="h-3.5 w-3.5 mr-1" />
                  Descargar PDF
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy || !pdfPreviewUrl}
                  onClick={handleOpenPreviewPdfTab}
                  data-testid="contrato-open-preview-pdf"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Abrir en pestaña
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={handleForceAudit}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Forzar auditoría
                </Button>
              </div>

              <section
                ref={previewSectionRef}
                className="rounded-md border border-slate-200 overflow-hidden bg-slate-50"
                data-testid="honorarios-contrato-pdf-preview"
              >
                <div className="px-3 py-2 border-b bg-white flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-medium text-slate-700 m-0">Vista previa PDF</p>
                  {pdfPreviewBlob ? (
                    <div className="flex flex-wrap gap-1.5">
                      <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={handleDownloadPreviewPdf} disabled={busy}>
                        <Download className="h-3 w-3 mr-1" />
                        Descargar
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={handleOpenPreviewPdfTab} disabled={busy || !pdfPreviewUrl}>
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Abrir
                      </Button>
                    </div>
                  ) : null}
                </div>
                <div className="min-h-[280px] flex flex-col items-center justify-center bg-slate-100">
                  {busy ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-12">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Generando vista previa…
                    </div>
                  ) : pdfPreviewUrl && pdfPreviewBlob && !embeddedPreview ? (
                    <>
                      <object
                        data={pdfPreviewUrl}
                        type="application/pdf"
                        className="w-full h-[480px] bg-white"
                        aria-label="Vista previa del contrato"
                      >
                        <p className="p-4 text-xs text-slate-600 text-center">
                          El visor embebido no está disponible. Use «Descargar» o «Abrir en pestaña».
                        </p>
                      </object>
                      <p className="text-[11px] text-slate-500 px-3 py-2 m-0 text-center">
                        Si no ve el PDF aquí, descárguelo o ábralo en el navegador del sistema.
                      </p>
                    </>
                  ) : pdfPreviewBlob && embeddedPreview ? (
                    <div className="py-10 px-6 text-center space-y-3 max-w-lg" data-testid="honorarios-pdf-embedded-fallback">
                      <p className="text-sm text-slate-700 m-0">
                        PDF generado. En el visor integrado no se embebe el documento.
                        Descárguelo o ábralo en el navegador del sistema.
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        <Button type="button" size="sm" onClick={handleDownloadPreviewPdf}>
                          <Download className="h-3.5 w-3.5 mr-1" />
                          Descargar PDF
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={handleOpenPreviewPdfTab}>
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          Abrir en pestaña
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-12 px-6 text-center m-0">
                      Pulse «Ver PDF del contrato» para generar la vista previa
                      {sampleClient ? " con datos del cliente de muestra" : " (usa datos de ejemplo si aún no hay cliente)"}.
                    </p>
                  )}
                </div>
              </section>

              <div
                className="rounded-md border border-dashed border-slate-300 bg-white p-4 space-y-3"
                data-testid="honorarios-plantilla-empresa-block"
              >
                <div>
                  <p className="text-xs font-medium m-0">Plantilla propia del despacho</p>
                  <p className="text-[11px] text-muted-foreground m-0 mt-1">
                    Suba su PDF o DOCX (con placeholders {"{{despacho_nombre}}"}, {"{{cliente_nombre}}"}, etc.).
                    El sistema lo usará al generar el contrato entre el prestador de servicios y el cliente,
                    igual que la plantilla de empresa en contratos laborales.
                  </p>
                </div>
                {template?.plantilla_empresa?.tiene_archivo ? (
                  <p className="text-xs text-emerald-800 m-0">
                    Archivo cargado:{" "}
                    <span className="font-medium">{template.plantilla_empresa.filename}</span>
                    {template.plantilla_empresa.uploaded_at
                      ? ` · ${String(template.plantilla_empresa.uploaded_at).slice(0, 10)}`
                      : ""}
                    {template.plantilla_empresa.extracted_chars
                      ? ` · ${template.plantilla_empresa.extracted_chars} caracteres importados al machote`
                      : ""}
                  </p>
                ) : (
                  <p className="text-xs text-slate-600 m-0">Sin plantilla propia cargada. Suba un PDF o DOCX.</p>
                )}
                <input
                  ref={plantillaInputRef}
                  type="file"
                  accept={ACCEPT_PLANTILLA}
                  className="hidden"
                  data-testid="honorarios-plantilla-file-input"
                  onChange={(e) => handleUploadPlantilla(e.target.files?.[0])}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={readOnly || uploadBusy}
                    onClick={() => plantillaInputRef.current?.click()}
                    data-testid="btn-subir-plantilla-honorarios"
                  >
                    {uploadBusy ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5 mr-1" />
                    )}
                    {template?.plantilla_empresa?.tiene_archivo ? "Reemplazar archivo" : "Seleccionar archivo"}
                  </Button>
                  {template?.plantilla_empresa?.tiene_archivo ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploadBusy}
                      onClick={handleDownloadPlantilla}
                      data-testid="btn-descargar-plantilla-honorarios"
                    >
                      <Download className="h-3.5 w-3.5 mr-1" />
                      Descargar plantilla
                    </Button>
                  ) : null}
                </div>
                <div className="space-y-2 pt-1 border-t border-slate-100">
                  <p className="text-[11px] font-medium text-slate-700 m-0">Modelo a usar al generar documentos</p>
                  <RadioGroup
                    value={template?.modelo_activo || "sistema"}
                    onValueChange={handleSelectModelo}
                    className="flex flex-col sm:flex-row gap-3 sm:gap-6"
                    disabled={readOnly || uploadBusy}
                  >
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <RadioGroupItem value="sistema" id="modelo-sistema-honorarios" />
                      <span>Modelo del sistema</span>
                    </label>
                    <label
                      className={`flex items-center gap-2 text-xs ${
                        template?.plantilla_empresa?.tiene_archivo
                          ? "cursor-pointer"
                          : "opacity-50 cursor-not-allowed"
                      }`}
                    >
                      <RadioGroupItem
                        value="empresa"
                        id="modelo-empresa-honorarios"
                        disabled={!template?.plantilla_empresa?.tiene_archivo}
                      />
                      <span>Modelo de la empresa</span>
                    </label>
                  </RadioGroup>
                </div>
              </div>
            </section>

            <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
              <div>
                <p className="text-sm font-medium text-[#0F2D5C] m-0">Machote editable del despacho</p>
                <p className="text-xs text-muted-foreground m-0 mt-1">
                  Edite el texto completo (placeholders {"{{…}}"}), el incremento anual y el correo que acompaña el PDF.
                  «Ver PDF del contrato» usa el texto del editor (aunque aún no guarde) y los datos de muestra o el cliente seleccionado.
                  Guarde el machote para usarlo al generar por cliente en «Generar / revisar».
                </p>
              </div>

              <div className="space-y-2" data-testid="contrato-variantes-selector">
                <Label className="text-xs">Tipo de contrato (según condición en Lista)</Label>
                <div className="flex flex-wrap gap-2">
                  {CONTRACT_VARIANT_OPTIONS.map((opt) => {
                    const active = machoteVariant === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => switchMachoteVariant(opt.id)}
                        className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                          active
                            ? "border-teal-600 bg-teal-50 text-teal-950 ring-1 ring-teal-500"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                        data-testid={`contrato-variante-${opt.id}`}
                      >
                        <span className="block font-semibold">{opt.label}</span>
                        <span className="mt-0.5 block text-[11px] text-slate-500">{opt.hint}</span>
                      </button>
                    );
                  })}
                </div>
                {machoteVariant !== "honorarios" ? (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                    <span>
                      {currentVariantMeta?.is_custom
                        ? "Esta variante tiene texto propio guardado."
                        : "Sin texto propio: al generar se usa el machote general (Honorarios). Puede cargar un modelo sugerido y guardarlo."}
                    </span>
                    {currentVariantMeta?.has_suggested_default && !currentVariantMeta?.is_custom ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={busy || readOnly}
                        onClick={handleLoadSuggestedVariant}
                        data-testid="contrato-cargar-sugerido"
                      >
                        Cargar modelo sugerido
                      </Button>
                    ) : null}
                    {currentVariantMeta?.is_custom ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-rose-700"
                        disabled={busy || readOnly}
                        onClick={() => void handleClearVariant()}
                        data-testid="contrato-limpiar-variante"
                      >
                        Quitar variante (usar general)
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 m-0">
                    El machote general se usa para honorarios con factura y como respaldo de las otras
                    variantes hasta que guarde un texto propio.
                  </p>
                )}
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <Label className="text-xs">Incremento anual (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    className="mt-1 h-8 text-xs rounded-sm"
                    value={increasePct}
                    disabled={readOnly || busy}
                    onChange={(e) => setIncreasePct(e.target.value)}
                    data-testid="contrato-incremento-pct"
                  />
                </div>
                <div className="sm:col-span-2 flex flex-wrap items-end gap-2">
                  {!readOnly ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 rounded-sm text-xs"
                        disabled={busy}
                        onClick={handleSaveTemplate}
                        data-testid="contrato-guardar-machote"
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                        {machoteVariant === "honorarios" ? "Guardar machote" : "Guardar variante"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-sm text-xs"
                        disabled={busy}
                        onClick={handleResetTemplate}
                      >
                        Restablecer predeterminado
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>

              <div>
                <Label className="text-xs">
                  Texto del contrato (placeholders {"{{…}}"})
                  {machoteVariant !== "honorarios"
                    ? ` — ${contractVariantLabel(machoteVariant)}`
                    : " — general"}
                </Label>
                <Textarea
                  className="mt-1 min-h-[280px] rounded-sm border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 font-mono resize-y"
                  value={body}
                  disabled={readOnly || busy}
                  onChange={(e) => setBody(e.target.value)}
                  spellCheck={false}
                  data-testid="contrato-machote-body"
                />
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">Asunto del correo</Label>
                  <Input
                    className="mt-1 h-8 text-xs rounded-sm"
                    value={emailSubject}
                    disabled={readOnly || busy}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    data-testid="contrato-email-subject"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Cuerpo del correo (adjunto = PDF del contrato)</Label>
                  <Textarea
                    className="mt-1 min-h-[120px] rounded-sm border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 resize-y"
                    value={emailBody}
                    disabled={readOnly || busy}
                    onChange={(e) => setEmailBody(e.target.value)}
                    spellCheck={false}
                    data-testid="contrato-email-body"
                  />
                </div>
              </div>
              {template?.placeholders?.length ? (
                <p className="m-0 text-[10px] text-slate-500">
                  Placeholders: {template.placeholders.map((p) => `{{${p}}}`).join(" · ")}
                </p>
              ) : null}
            </section>
          </div>
        </TabsContent>

        <TabsContent value={SUB_CONTRATOS}>
          <DespachoSectionCard
            title="Contratos por condición del cliente"
            subtitle="Aquí se genera, envía y archiva el PDF firmado (con historial). Honorarios y fecha se precargan de Lista; al corregirlos quedan fijos (manual). Cortesía y miembros de convenio cubiertos no cobran."
          >
            <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="contratos-variant-filters">
              {CONTRATOS_VARIANT_FILTERS.map((f) => {
                const count = contratosCounts[f.id] ?? 0;
                const active = contratosVariantFilter === f.id;
                return (
                  <Button
                    key={f.id}
                    type="button"
                    size="sm"
                    variant={active ? "default" : "outline"}
                    className={`h-7 rounded-sm text-xs ${active ? "bg-[#0F2D5C] hover:bg-[#0a2247]" : ""}`}
                    onClick={() => setContratosVariantFilter(f.id)}
                    data-testid={`contratos-filtro-${f.id}`}
                  >
                    {f.label}
                    <span className="ml-1 opacity-80">({count})</span>
                  </Button>
                );
              })}
              <div
                className="relative ml-auto min-w-[200px] max-w-xs flex-1 sm:flex-none"
                data-testid="contratos-search-wrap"
              >
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input
                  type="search"
                  value={contratosSearch}
                  onChange={(e) => setContratosSearch(e.target.value)}
                  placeholder="Buscar por nombre o RFC…"
                  className="h-7 rounded-sm pl-7 text-xs"
                  aria-label="Buscar contratos por nombre o RFC"
                  data-testid="contratos-search"
                />
              </div>
            </div>

            <p className="m-0 mb-2 text-[11px] text-slate-500">
              Machote: edite cada tipo en «Machote». Al generar/enviar se usa la variante detectada.
              Cortesía = sin honorarios (cortesía temporal). Convenio/otros = cobro según acuerdo
              (pagador / hasta confirmar fecha de pago).
            </p>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
              </div>
            ) : (
              <div className="overflow-x-auto rounded-sm border border-slate-200" data-testid="contratos-por-condicion-table">
                <table className="w-full text-[11px] min-w-[980px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left">
                      <th className="p-2 font-medium">Cliente</th>
                      <th className="p-2 font-medium">RFC</th>
                      <th className="p-2 font-medium">Condición / cobro</th>
                      <th className="p-2 font-medium">Tipo de contrato</th>
                      <th className="p-2 font-medium" data-testid="contratos-col-honorarios">Honorarios</th>
                      <th className="p-2 font-medium" data-testid="contratos-col-fecha">Fecha contrato</th>
                      <th className="p-2 font-medium">Estado</th>
                      <th className="p-2 font-medium w-56">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contratosItems.length ? contratosItems.map((row) => {
                      const busyRow = rowBusy === row.fiscal_client_id;
                      const variantLbl = rowContractVariantLabel(row);
                      const canEditFee = feeEditableForRow(row) && canViewAmounts && !readOnly;
                      const feeVal = feeDraftValue(row);
                      const dateVal = dateDraftValue(row);
                      const payNote = paymentConditionNote(row);
                      const feeDirty = Object.prototype.hasOwnProperty.call(feeDrafts, row.fiscal_client_id);
                      const dateDirty = Object.prototype.hasOwnProperty.call(dateDrafts, row.fiscal_client_id);
                      const dirty = feeDirty || dateDirty;
                      return (
                        <tr
                          key={row.fiscal_client_id}
                          className="border-b border-slate-100 last:border-0"
                          data-testid={`contratos-row-${row.fiscal_client_id}`}
                        >
                          <td className="p-2 font-medium">{row.client_name || "—"}</td>
                          <td className="p-2 font-mono">{row.client_rfc || "—"}</td>
                          <td className="p-2">
                            <Badge
                              variant="outline"
                              className="rounded-sm text-[10px] font-normal"
                              title={CONDITION_FULL_LABELS[row.billing_condition] || row.billing_condition}
                            >
                              {conditionShortLabel(row.billing_condition)}
                            </Badge>
                            {payNote ? (
                              <span className="block text-[10px] text-slate-500 mt-0.5 max-w-[180px]" title={payNote}>
                                {payNote}
                              </span>
                            ) : null}
                          </td>
                          <td className="p-2 text-[#0F2D5C] font-medium" data-testid={`contratos-variante-${row.fiscal_client_id}`}>
                            {variantLbl}
                          </td>
                          <td className="p-2 whitespace-nowrap align-top">
                            {canEditFee ? (
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                className="h-7 w-[110px] text-xs rounded-sm"
                                value={feeVal}
                                disabled={busyRow}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setFeeDrafts((d) => ({ ...d, [row.fiscal_client_id]: v }));
                                }}
                                onBlur={() => {
                                  if (!feeDirty) return;
                                  void saveContratoRosterFields(row, { fee: feeVal });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.currentTarget.blur();
                                  }
                                }}
                                data-testid={`contratos-fee-${row.fiscal_client_id}`}
                                title={row.fee_manual ? "Honorario fijo (manual). No lo pisa el último CFDI." : "Precargado. Al guardar queda fijo (manual)."}
                              />
                            ) : (
                              <span
                                className="text-slate-700"
                                data-testid={`contratos-fee-${row.fiscal_client_id}`}
                                title={payNote || undefined}
                              >
                                {!canViewAmounts
                                  ? "•••"
                                  : (String(row.billing_condition || "") === "cortesia"
                                    || isConvenioCoveredMember(row)
                                    ? "Sin cobro"
                                    : money(row.monthly_fee))}
                              </span>
                            )}
                            {row.fee_manual && canEditFee ? (
                              <span className="block text-[9px] text-emerald-700 mt-0.5">Fijo</span>
                            ) : null}
                          </td>
                          <td className="p-2 whitespace-nowrap align-top">
                            {!readOnly ? (
                              <Input
                                type="date"
                                className="h-7 w-[140px] text-xs rounded-sm"
                                value={dateVal}
                                disabled={busyRow}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setDateDrafts((d) => ({ ...d, [row.fiscal_client_id]: v }));
                                }}
                                onBlur={() => {
                                  if (!dateDirty) return;
                                  void saveContratoRosterFields(row, { date: dateVal });
                                }}
                                data-testid={`contratos-fecha-${row.fiscal_client_id}`}
                                title="Fecha de inicio del contrato (Lista)"
                              />
                            ) : (
                              <span>{dateVal || "—"}</span>
                            )}
                          </td>
                          <td className="p-2">{statusBadge(row)}</td>
                          <td className="p-2">
                            <div className="flex flex-wrap gap-1">
                              {dirty && !readOnly ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-1.5 text-[10px] rounded-sm"
                                  disabled={busyRow}
                                  onClick={() => void saveContratoRosterFields(row, {
                                    fee: feeDirty ? feeVal : undefined,
                                    date: dateDirty ? dateVal : undefined,
                                  })}
                                  data-testid={`contratos-guardar-${row.fiscal_client_id}`}
                                >
                                  <Save className="h-3 w-3 mr-0.5" />
                                  Guardar
                                </Button>
                              ) : null}
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 px-1.5 text-[10px] rounded-sm"
                                onClick={() => {
                                  selectClientForContract(row);
                                  setSubTab(SUB_GENERAR);
                                }}
                                data-testid={`contratos-revisar-${row.fiscal_client_id}`}
                              >
                                Revisar
                              </Button>
                              {!readOnly ? (
                                <>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-1.5 text-[10px] rounded-sm"
                                    disabled={busyRow || busy}
                                    title={`Generar PDF · ${variantLbl}`}
                                    onClick={() => withRow(row.fiscal_client_id, async () => {
                                      selectClientForContract(row);
                                      const start = dateDraftValue(row) || undefined;
                                      const response = await honorariosApi.generateServiceContract(
                                        row.fiscal_client_id,
                                        {
                                          fechaInicio: start,
                                          allowIncomplete: true,
                                        },
                                      );
                                      downloadBlobResponse(response, "contrato.pdf");
                                      toast.success(`PDF generado (${variantLbl}).`);
                                    })}
                                    data-testid={`contratos-generar-${row.fiscal_client_id}`}
                                  >
                                    <FileText className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-1.5 text-[10px] rounded-sm"
                                    disabled={busyRow || !row.email}
                                    title={row.email ? `Revisar y enviar · ${variantLbl}` : "Sin correo"}
                                    onClick={() => setSendContractRow(row)}
                                    data-testid={`contratos-enviar-${row.fiscal_client_id}`}
                                  >
                                    <Mail className="h-3 w-3" />
                                  </Button>
                                  <input
                                    ref={(el) => { fileRefs.current[row.fiscal_client_id] = el; }}
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      e.target.value = "";
                                      if (!f) return;
                                      withRow(row.fiscal_client_id, async () => {
                                        await honorariosApi.uploadServiceContractSigned(row.fiscal_client_id, f);
                                        toast.success("PDF firmado agregado al historial (se conservan los anteriores).");
                                      });
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-1.5 text-[10px] rounded-sm"
                                    disabled={busyRow}
                                    onClick={() => fileRefs.current[row.fiscal_client_id]?.click()}
                                    title="Subir PDF firmado (se agrega al historial)"
                                    data-testid={`contratos-upload-${row.fiscal_client_id}`}
                                  >
                                    <Upload className="h-3 w-3" />
                                  </Button>
                                  {row.tiene_contrato_firmado ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-1.5 text-[10px] rounded-sm text-emerald-800"
                                      disabled={busyRow}
                                      onClick={() => setSignedHistoryRow(row)}
                                      title="Ver historial de PDFs firmados"
                                      data-testid={`contratos-historial-${row.fiscal_client_id}`}
                                    >
                                      <History className="h-3 w-3" />
                                    </Button>
                                  ) : null}
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={8} className="p-4 text-center text-slate-500">
                          {contratosSearch.trim()
                            ? "Sin resultados para esa búsqueda. Pruebe otro nombre o RFC, o limpie el buscador."
                            : "No hay clientes en este filtro. Clasifíquelos en Lista de clientes (Condición) o cambie el filtro de arriba."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </DespachoSectionCard>
        </TabsContent>

        <TabsContent value={SUB_GENERAR}>
          <DespachoSectionCard
            title="Generar y revisar contrato"
            subtitle="Elija el cliente (datos de la ficha: nombre, RFC, domicilio, representante). La fecha de inicio del PDF sale de Lista («Inicio operaciones / contrato»). El tipo de contrato se detecta solo según la condición."
          >
            <div className="grid gap-2 sm:grid-cols-3 mb-3">
              <div className="sm:col-span-3">
                <Label className="text-xs">Cliente</Label>
                <Select
                  value={genClientId || undefined}
                  onValueChange={(v) => {
                    setGenClientId(v);
                    setPreviewText("");
                    setPreviewMeta(null);
                    setValidation(null);
                    const row = items.find((r) => r.fiscal_client_id === v);
                    const start = (
                      row?.fecha_inicio_contrato
                      || row?.operations_start_at
                      || ""
                    ).slice(0, 10);
                    if (start) setFechaInicio(start);
                  }}
                >
                  <SelectTrigger className="mt-1 h-8 text-xs rounded-sm" data-testid="contrato-gen-cliente">
                    <SelectValue placeholder="Seleccione cliente…" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientOptions.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedRow ? (
              <div className="mb-3 rounded-sm border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700 space-y-0.5">
                <p className="m-0 font-medium text-[#0F2D5C]">{selectedRow.client_name}</p>
                <p className="m-0 font-mono">RFC: {selectedRow.client_rfc || "—"}</p>
                <p className="m-0">Correo: {selectedRow.email || "—"}</p>
                <p className="m-0" data-testid="contrato-fecha-inicio-lista">
                  Inicio del contrato (Lista):{" "}
                  <span className="font-medium">
                    {(
                      selectedRow.fecha_inicio_contrato
                      || selectedRow.operations_start_at
                      || fechaInicio
                      || ""
                    ).slice(0, 10) || "—"}
                  </span>
                </p>
                <p className="m-0">
                  Tipo de contrato:{" "}
                  <span className="font-medium text-[#0F2D5C]" data-testid="contrato-gen-variante">
                    {selectedContractVariantLabel}
                  </span>
                </p>
                <p className="m-0">
                  Honorarios: {canViewAmounts ? money(selectedRow.monthly_fee) : "•••"} · {statusBadge(selectedRow)}
                </p>
                <p className="m-0 text-[10px] text-slate-500">
                  Nombre, RFC, domicilio, CURP, correo, teléfono y representante legal se toman del alta del cliente.
                  La variante se toma de la condición en Lista (factura → honorarios, convenio/otros → convenio, cortesía → cortesía).
                </p>
              </div>
            ) : null}

            {validation ? (
              <div
                className={`mb-3 rounded-sm border p-2 text-[11px] ${
                  validation.ok
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-amber-200 bg-amber-50 text-amber-950"
                }`}
                data-testid="contrato-validacion"
              >
                {validation.ok ? (
                  <p className="m-0 font-medium">Datos indispensables completos.</p>
                ) : (
                  <>
                    <p className="m-0 font-medium">
                      Faltan {validation.required_count} dato(s) indispensable(s):
                    </p>
                    <ul className="m-0 mt-1 pl-4 list-disc">
                      {(validation.required || []).map((item) => (
                        <li key={`${item.party}-${item.field}`}>
                          [{item.party}] {item.label}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {(validation.recommended || []).length ? (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[10px]">
                      Recomendados ({validation.recommended_count})
                    </summary>
                    <ul className="m-0 mt-1 pl-4 list-disc text-[10px]">
                      {validation.recommended.map((item) => (
                        <li key={`rec-${item.party}-${item.field}`}>
                          [{item.party}] {item.label}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 rounded-sm text-xs"
                disabled={busy || !genClientId}
                onClick={handleReviewText}
                data-testid="contrato-revisar-texto"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Revisar contrato lleno
              </Button>
              {!readOnly ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 rounded-sm text-xs"
                    disabled={busy || !genClientId}
                    onClick={() => handleGenerate(false)}
                    data-testid="contrato-generar-pdf"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    Generar PDF
                  </Button>
                  {validation && !validation.ok ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 rounded-sm text-xs border-amber-400 text-amber-900"
                      disabled={busy || !genClientId}
                      onClick={() => handleGenerate(true)}
                      data-testid="contrato-generar-incompleto"
                    >
                      Generar de todos modos
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-sm text-xs"
                    disabled={busy || !genClientId || !selectedRow?.email}
                    onClick={() => handleSendSelected(selectedRow?.email_status === "sent")}
                    data-testid="contrato-enviar-correo"
                    title="Pide confirmación antes de enviar"
                  >
                    <Mail className="h-3.5 w-3.5 mr-1" />
                    Enviar por correo
                  </Button>
                </>
              ) : null}
            </div>

            {previewText ? (
              <div className="space-y-1">
                <Label className="text-xs">Contrato lleno (revisión)</Label>
                {previewMeta ? (
                  <p className="m-0 text-[10px] text-slate-500">
                    Prestador: {previewMeta.context?.despacho_nombre || "—"} · Cliente: {previewMeta.client_name} ·
                    Inicio: {previewMeta.context?.fecha_inicio || fechaInicio}
                    {previewMeta.contract_variant_label || previewMeta.context?.contract_variant_label
                      ? ` · Tipo: ${previewMeta.contract_variant_label || previewMeta.context?.contract_variant_label}`
                      : ""}
                  </p>
                ) : null}
                <textarea
                  readOnly
                  className="w-full min-h-[280px] rounded-sm border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 font-mono resize-y"
                  value={previewText}
                  data-testid="contrato-preview-text"
                />
              </div>
            ) : (
              <p className="m-0 text-xs text-slate-500">
                Pulse «Revisar contrato lleno» para ver el machote con los datos del despacho y del cliente.
              </p>
            )}
          </DespachoSectionCard>

          <DespachoSectionCard
            title="Clientes y contrato"
            subtitle={`${summary.without_signed ?? 0} sin firmado · ${summary.signed ?? 0} firmados · ${summary.total ?? 0} en lista. La carga y el historial de PDFs firmados están en la subpestaña Contratos.`}
          >
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Label className="text-xs">Mostrar</Label>
              <Select value={listFilter} onValueChange={setListFilter}>
                <SelectTrigger className="h-8 w-[200px] text-xs rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin_contrato">Sin contrato firmado</SelectItem>
                  <SelectItem value="borrador">Solo borrador</SelectItem>
                  <SelectItem value="firmado">Firmados</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
              </div>
            ) : (
              <div className="overflow-x-auto rounded-sm border border-slate-200">
                <table className="w-full text-[11px] min-w-[820px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left">
                      <th className="p-2 font-medium">Cliente</th>
                      <th className="p-2 font-medium">RFC</th>
                      <th className="p-2 font-medium">Correo</th>
                      <th className="p-2 font-medium">Estado</th>
                      <th className="p-2 font-medium w-48">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length ? filteredItems.map((row) => {
                      const busyRow = rowBusy === row.fiscal_client_id;
                      return (
                        <tr key={row.fiscal_client_id} className="border-b border-slate-100 last:border-0">
                          <td className="p-2 font-medium">{row.client_name || "—"}</td>
                          <td className="p-2 font-mono">{row.client_rfc || "—"}</td>
                          <td className="p-2 max-w-[140px] truncate">{row.email || "—"}</td>
                          <td className="p-2">{statusBadge(row)}</td>
                          <td className="p-2">
                            <div className="flex flex-wrap gap-1">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 px-1.5 text-[10px] rounded-sm"
                                onClick={() => {
                                  setGenClientId(row.fiscal_client_id);
                                  const start = (
                                    row.fecha_inicio_contrato
                                    || row.operations_start_at
                                    || ""
                                  ).slice(0, 10);
                                  if (start) setFechaInicio(start);
                                  setSubTab(SUB_GENERAR);
                                }}
                              >
                                Revisar
                              </Button>
                              {!readOnly ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-1.5 text-[10px] rounded-sm"
                                  disabled={busyRow || !row.email}
                                  onClick={() => setSendContractRow(row)}
                                  title="Revisar y enviar por correo"
                                >
                                  <Mail className="h-3 w-3" />
                                </Button>
                              ) : null}
                              {!readOnly ? (
                                <>
                                  <input
                                    ref={(el) => { fileRefs.current[`gen-${row.fiscal_client_id}`] = el; }}
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      e.target.value = "";
                                      if (!f) return;
                                      withRow(row.fiscal_client_id, async () => {
                                        await honorariosApi.uploadServiceContractSigned(row.fiscal_client_id, f);
                                        toast.success("PDF firmado agregado al historial.");
                                      });
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-1.5 text-[10px] rounded-sm"
                                    disabled={busyRow}
                                    onClick={() => fileRefs.current[`gen-${row.fiscal_client_id}`]?.click()}
                                    title="Subir firmado (historial)"
                                  >
                                    <Upload className="h-3 w-3" />
                                  </Button>
                                </>
                              ) : null}
                              {row.tiene_contrato_firmado ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-1.5 text-[10px] rounded-sm text-emerald-800"
                                  disabled={busyRow}
                                  onClick={() => setSignedHistoryRow(row)}
                                  title="Historial de PDFs firmados"
                                >
                                  <History className="h-3 w-3" />
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={5} className="p-4 text-center text-slate-500">
                          No hay clientes en este filtro. Revise la Lista de clientes de honorarios.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </DespachoSectionCard>
        </TabsContent>
      </Tabs>

      <Dialog
        open={Boolean(sendContractRow)}
        onOpenChange={(open) => {
          if (!open) closeSendContractDialog();
        }}
      >
        <DialogContent
          className="rounded-sm max-w-md"
          data-testid="honorarios-send-contrato-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[#0F2D5C]">Revisar y enviar contrato</DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              El correo no se envía hasta que confirme. Revise el PDF y los datos del destinatario.
            </DialogDescription>
          </DialogHeader>

          {sendContractRow ? (
            <div className="space-y-2 text-xs text-slate-700 py-1" data-testid="honorarios-send-contrato-summary">
              <p className="m-0">
                <span className="text-slate-500">Cliente:</span>{" "}
                <span className="font-medium text-[#0F2D5C]">
                  {sendContractRow.client_name || "—"}
                </span>
              </p>
              <p className="m-0 font-mono">
                <span className="text-slate-500 font-sans">RFC:</span>{" "}
                {sendContractRow.client_rfc || sendContractRow.rfc || "—"}
              </p>
              <p className="m-0">
                <span className="text-slate-500">Correo:</span>{" "}
                <span className="font-medium">{sendContractRow.email || "—"}</span>
              </p>
              <p className="m-0">
                <span className="text-slate-500">Inicio del contrato:</span>{" "}
                {sendContractFechaInicio || "—"}
              </p>
              <p className="m-0" data-testid="honorarios-send-contrato-variante">
                <span className="text-slate-500">Tipo de contrato:</span>{" "}
                <span className="font-medium text-[#0F2D5C]">
                  {rowContractVariantLabel(sendContractRow)}
                </span>
              </p>
              {sendValidationBusy ? (
                <p className="m-0 text-[10px] text-slate-500 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Validando datos del contrato…
                </p>
              ) : null}
              {sendValidation && sendValidation.ok === false ? (
                <div
                  className="rounded-sm border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-950 space-y-1"
                  data-testid="honorarios-send-contrato-validation"
                >
                  <p className="m-0 font-medium">
                    Aviso: faltan {(sendValidation.required || []).length} dato(s) mínimo(s)
                    {(sendValidation.recommended || []).length
                      ? ` · ${(sendValidation.recommended || []).length} recomendado(s)`
                      : ""}
                    :
                  </p>
                  <ul className="m-0 pl-4 list-disc">
                    {(sendValidation.required || []).map((item) => (
                      <li key={`${item.party}-${item.field}`}>
                        [{item.party === "prestador" ? "despacho" : "cliente"}] {item.label}
                      </li>
                    ))}
                    {(sendValidation.recommended || []).slice(0, 4).map((item) => (
                      <li key={`rec-${item.party}-${item.field}`} className="text-amber-900/80">
                        (recomendado) [{item.party === "prestador" ? "despacho" : "cliente"}] {item.label}
                      </li>
                    ))}
                  </ul>
                  <p className="m-0 text-[10px] text-amber-900/90">
                    Puede completar Datos del despacho o la ficha del cliente. Si ya revisó el PDF,
                    «Confirmar envío» mandará el contrato de todos modos.
                  </p>
                </div>
              ) : null}
              {sendValidation?.ok ? (
                <p className="m-0 text-[10px] text-emerald-700">
                  Datos indispensables completos. Puede ver el PDF y confirmar el envío.
                </p>
              ) : null}
              <p className="m-0 text-[10px] text-slate-500">
                Pulse «Ver PDF» para revisar el documento. Solo «Confirmar envío» manda el correo.
              </p>
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm text-xs"
              disabled={sendContractBusy || sendPreviewBusy || !sendContractRow}
              onClick={handlePreviewSendContractPdf}
              data-testid="honorarios-send-contrato-preview"
            >
              {sendPreviewBusy
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <FileText className="h-3.5 w-3.5 mr-1" />}
              Ver PDF
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm text-xs"
              disabled={sendContractBusy || sendPreviewBusy}
              onClick={closeSendContractDialog}
              data-testid="honorarios-send-contrato-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-sm text-xs bg-[#0F2D5C] hover:bg-[#0a2247]"
              disabled={
                sendContractBusy
                || sendPreviewBusy
                || sendValidationBusy
                || !sendContractRow?.email
              }
              onClick={handleConfirmSendContract}
              data-testid="honorarios-send-contrato-confirm"
            >
              {sendContractBusy
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <Mail className="h-3.5 w-3.5 mr-1" />}
              {sendValidation?.ok === false ? "Enviar de todos modos" : "Confirmar envío"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(signedHistoryRow)}
        onOpenChange={(open) => {
          if (!open) setSignedHistoryRow(null);
        }}
      >
        <DialogContent
          className="rounded-sm max-w-lg"
          data-testid="contratos-historial-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[#0F2D5C]">PDFs firmados</DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Se conservan los archivos anteriores al subir uno nuevo. Puede descargar o eliminar
              cada versión.
            </DialogDescription>
          </DialogHeader>
          {signedHistoryRow ? (
            <div className="space-y-2 text-xs">
              <p className="m-0 font-medium text-[#0F2D5C]">
                {signedHistoryRow.client_name || "Cliente"}
                {signedHistoryRow.client_rfc ? (
                  <span className="font-mono font-normal text-slate-600"> · {signedHistoryRow.client_rfc}</span>
                ) : null}
              </p>
              {signedHistoryFiles.length ? (
                <ul className="m-0 p-0 list-none space-y-1.5" data-testid="contratos-historial-list">
                  {signedHistoryFiles.map((file, idx) => (
                    <li
                      key={file.file_id || `${file.filename}-${idx}`}
                      className="flex flex-wrap items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1.5"
                      data-testid={`contratos-historial-item-${file.file_id || idx}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="m-0 truncate font-medium text-slate-800" title={file.filename}>
                          {file.filename || "contrato_firmado.pdf"}
                          {idx === 0 ? (
                            <span className="ml-1 text-[10px] font-normal text-emerald-700">(actual)</span>
                          ) : null}
                        </p>
                        <p className="m-0 text-[10px] text-slate-500">
                          {file.uploaded_at
                            ? String(file.uploaded_at).replace("T", " ").slice(0, 19)
                            : "Sin fecha"}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-1.5 text-[10px] rounded-sm"
                        disabled={rowBusy === signedHistoryRow.fiscal_client_id}
                        onClick={() => withRow(signedHistoryRow.fiscal_client_id, async () => {
                          const fid = file.file_id && file.file_id !== "latest" ? file.file_id : undefined;
                          const response = await honorariosApi.downloadServiceContractSigned(
                            signedHistoryRow.fiscal_client_id,
                            fid,
                          );
                          downloadBlobResponse(response, file.filename || "contrato_firmado.pdf");
                        })}
                        title="Descargar"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                      {!readOnly && file.file_id && file.file_id !== "latest" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-1.5 text-[10px] rounded-sm text-red-700"
                          disabled={rowBusy === signedHistoryRow.fiscal_client_id}
                          onClick={() => {
                            const ok = window.confirm(
                              `¿Eliminar «${file.filename || "este PDF"}» del historial?`,
                            );
                            if (!ok) return;
                            withRow(signedHistoryRow.fiscal_client_id, async () => {
                              await honorariosApi.deleteServiceContractSignedFile(
                                signedHistoryRow.fiscal_client_id,
                                file.file_id,
                              );
                              toast.success("PDF eliminado del historial.");
                            });
                          }}
                          title="Eliminar del historial"
                          data-testid={`contratos-historial-delete-${file.file_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="m-0 text-slate-500">No hay PDFs firmados para este cliente.</p>
              )}
              {!readOnly ? (
                <div className="pt-1">
                  <input
                    ref={(el) => {
                      if (signedHistoryRow?.fiscal_client_id) {
                        fileRefs.current[`hist-${signedHistoryRow.fiscal_client_id}`] = el;
                      }
                    }}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f || !signedHistoryRow?.fiscal_client_id) return;
                      withRow(signedHistoryRow.fiscal_client_id, async () => {
                        await honorariosApi.uploadServiceContractSigned(
                          signedHistoryRow.fiscal_client_id,
                          f,
                        );
                        toast.success("PDF agregado; se conservan los anteriores.");
                      });
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px] rounded-sm"
                    disabled={rowBusy === signedHistoryRow.fiscal_client_id}
                    onClick={() => fileRefs.current[`hist-${signedHistoryRow.fiscal_client_id}`]?.click()}
                    data-testid="contratos-historial-upload"
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Agregar otro PDF firmado
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm text-xs"
              onClick={() => setSignedHistoryRow(null)}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
